import data from '../access.json' with { type: 'json' };

/**
 * Каталог возможностей: что доступно, кому и почему нет.
 *
 * Тариф — лишь одна из причин недоступности, и смешивать его с остальными
 * нельзя. «Функция ещё не сделана», «не хватает данных» и «нет сети» — это
 * не повод предлагать оплату, а оплаченный доступ не чинит отсутствие
 * истории. Поэтому у каждой возможности четыре независимых признака.
 *
 * Каталог общий для интерфейса, сервера и сравнения тарифов: три копии этих
 * правил разъехались бы в первый же месяц, и человек увидел бы замок там, где
 * его нет, — или наоборот.
 */

export type Tier = 'guest' | 'free' | 'trial' | 'pro';

export type FeatureKey =
  /** Полная картина: счета, доходы, долги, платежи любой периодичности. */
  | 'full_picture'
  /** Ответ «сколько можно потратить» и календарь платежей. */
  | 'daily_answer'
  /** План периода, факт против плана, ручное распределение. */
  | 'period_plan'
  /** Цели и подушка без тарифного лимита. */
  | 'goals'
  /** Первый ИИ-разбор — обещание продукта, оплатой не закрывается. */
  | 'first_analysis'
  /** Проверка покупки и погашения: считает ядро, тарифицировать нечего. */
  | 'scenarios'
  /** Дополнительные ИИ-разборы сверх первого. */
  | 'extra_analysis'
  /** Сравнение вариантов рядом и библиотека сценариев. */
  | 'compare_scenarios'
  /** Запись операции вручную: клавиатура, категории, счёт. Никогда не платная. */
  | 'manual_entry'
  /** Запись операции текстом: «1200 продукты» разбирает модель. */
  | 'phrase_input'
  /** Разбор фотографии чека. */
  | 'receipt_photo'
  /** Загрузка электронного чека: PDF и изображения. */
  | 'receipt_import'
  /** Разбор прошлого периода словами: наблюдение и что оно даст целям. */
  | 'period_review'
  /** «Что видно за период» под графиком Аналитики. */
  | 'chart_notes'
  /** Назначение будущего дохода на платежи и цели. */
  | 'future_income'
  /** Принятые версии плана и ручные изменения. */
  | 'plan_versions'
  /** Расширенная ретроспектива и автоматические обзоры. */
  | 'retrospective'
  /** Автоматические правила резервирования. */
  | 'auto_rules'
  /** Экспорт своих данных и восстановление. */
  | 'export';

export interface FeatureState {
  /** Возможность реализована. Неготовое помечается «Позже», а не «Pro». */
  released: boolean;
  /** Тариф разрешает. */
  entitled: boolean;
  /** Данных достаточно, чтобы это имело смысл. */
  dataReady: boolean;
  /** Нужна сеть, и она есть. */
  online: boolean;
}

export interface Feature {
  key: FeatureKey;
  title: string;
  /** Кому доступно. Пустой список — доступно всем, включая гостя. */
  tiers: Tier[];
  /** Реализовано ли на сегодня. */
  released: boolean;
  /** Нужна ли сеть. */
  needsNetwork: boolean;
  /** Списывает ли квоту и какую. */
  meter?: 'analysis' | 'receipt' | 'phrase';
}

/**
 * Что нельзя закрывать оплатой ни при каких обстоятельствах.
 *
 * Предупреждение о дефиците, календарь платежей и влияние введённых фактов —
 * это и есть правдивость картины. Продавать её означает продавать доверие.
 */
export const NEVER_PAID = data.neverPaid as FeatureKey[];

export const CATALOG: readonly Feature[] = (data.features as Feature[]);

/** Сколько дней длится пробный период. Бывает один раз на аккаунт. */
export const TRIAL_DAYS = data.trialDays as number;

/**
 * Объёмы на месяц подписки (у пробного — на весь период), по себестоимости.
 *
 * Гость получает ровно один стартовый разбор — обещание из онбординга.
 */
export const QUOTAS = data.quotas as Record<
  Tier, { analysis: number; receipt: number; phrase: number }
>;

export interface AccessQuery {
  feature: FeatureKey;
  tier: Tier;
  /** Данных достаточно для осмысленного ответа. */
  dataReady?: boolean;
  online?: boolean;
  /** Сколько единиц соответствующей квоты уже израсходовано. */
  used?: number;
}

export type AccessVerdict =
  | { allowed: true }
  /**
   * Причина отказа. Оплату предлагаем **только** при `reason: 'tier'`.
   *
   * `signup` — отдельная причина именно поэтому: гостю нужен аккаунт, а не
   * деньги, и предложить ему купить значило бы взять плату за бесплатное.
   */
  | {
    allowed: false;
    reason: 'not_released' | 'tier' | 'signup' | 'no_data' | 'offline' | 'quota';
    message: string;
  };

/**
 * Можно ли этим воспользоваться.
 *
 * Порядок причин важен: неготовую функцию нельзя продавать, а нехватку данных
 * нельзя выдавать за тарифное ограничение. Оплату предлагаем в одном случае —
 * когда возможность готова, данные есть, сеть есть, а тариф не позволяет.
 */
export function checkAccess(q: AccessQuery): AccessVerdict {
  const f = CATALOG.find((x) => x.key === q.feature);
  if (!f) return { allowed: false, reason: 'not_released', message: 'Такой возможности нет' };

  if (!f.released) {
    return { allowed: false, reason: 'not_released', message: 'Позже' };
  }
  if (q.dataReady === false) {
    return {
      allowed: false, reason: 'no_data',
      message: 'Пока не хватает данных — оплата этого не изменит',
    };
  }
  if (f.needsNetwork && q.online === false) {
    return { allowed: false, reason: 'offline', message: 'Нужна сеть' };
  }
  if (f.tiers.length > 0 && !f.tiers.includes(q.tier)) {
    // Гостю первым шагом нужен аккаунт, а не деньги: пробный период и Pro
    // живут на аккаунте, и предлагать оплату раньше регистрации — значит
    // звать платить за то, к чему ещё нет доступа.
    if (q.tier === 'guest') {
      return {
        allowed: false, reason: 'signup',
        message: f.tiers.includes('free')
          ? 'Продолжим после регистрации — это бесплатно'
          : 'Сначала аккаунт, дальше — пробный период или Pro',
      };
    }
    return { allowed: false, reason: 'tier', message: 'Доступно в Pro' };
  }
  if (f.meter && q.used !== undefined) {
    const limit = QUOTAS[q.tier][f.meter];
    if (q.used >= limit) {
      return {
        allowed: false, reason: 'quota',
        // Каждому счётчику своя правда о том, что осталось доступным:
        // общая фраза «лимит исчерпан» не говорит человеку, что делать.
        message: f.meter === 'analysis'
          ? 'Разборы с ИИ закончились. Расчёты и записи доступны'
          : f.meter === 'phrase'
            ? 'Записи голосом и словами закончились. Ручной ввод доступен'
            : 'Разборы чеков закончились. Ручной ввод доступен',
      };
    }
  }
  return { allowed: true };
}

/** Остаток квоты — для подписи «осталось 2 из 10». */
export const remainingQuota = (tier: Tier, meter: 'analysis' | 'receipt' | 'phrase', used: number): number =>
  Math.max(0, QUOTAS[tier][meter] - used);

/** Окно квоты без подписки: календарный месяц. Смена устройства расход не обнуляет. */
export const quotaWindow = (isoDate: string): string => isoDate.slice(0, 7);

/** День `day` месяца, а в коротком месяце — его последний день: 31-е в феврале — 28-е или 29-е. */
function clampDay(year: number, month: number, day: number): string {
  const last = new Date(Date.UTC(year, month, 0)).getUTCDate();
  return `${year}-${String(month).padStart(2, '0')}-${String(Math.min(day, last)).padStart(2, '0')}`;
}

/**
 * Начало месяца подписки, в который попадает `day`.
 *
 * Объёмы Pro обновляются в число оплаты, а не 1-го (решение владельца
 * 21.10.2026): оплатил 21-го — новые 21-го каждого месяца, и у годовой
 * подписки тоже. Число берётся из исходной даты, без дрейфа: 31 января →
 * 28 февраля → 31 марта.
 */
export function cycleStart(anchor: string, day: string): string {
  const d = Number(anchor.slice(8, 10));
  let y = Number(day.slice(0, 4));
  let m = Number(day.slice(5, 7));
  let start = clampDay(y, m, d);
  if (start > day) {
    m -= 1;
    if (m === 0) { m = 12; y -= 1; }
    start = clampDay(y, m, d);
  }
  return start < anchor ? anchor : start;
}

/** Когда обновятся объёмы: начало следующего месяца подписки. */
export function nextCycle(anchor: string, day: string): string {
  const start = cycleStart(anchor, day);
  let y = Number(start.slice(0, 4));
  let m = Number(start.slice(5, 7)) + 1;
  if (m === 13) { m = 1; y += 1; }
  return clampDay(y, m, Number(anchor.slice(8, 10)));
}

/**
 * Окно учёта для уровня.
 *
 * У Pro окно — месяц подписки от дня оплаты (`anchor`). У пробного периода
 * пакет выдан **на весь период**: пробный бывает раз на аккаунт, поэтому окно
 * одно на всё время. Без подписки — календарный месяц: платных объёмов там
 * нет, окно нужно только для порядка.
 */
export const windowFor = (tier: Tier, day: string, anchor?: string | null): string =>
  (tier === 'trial' ? 'trial'
    : tier === 'pro' && anchor ? `c:${cycleStart(anchor, day)}`
      : quotaWindow(day));

/**
 * Окно стартового права.
 *
 * Первый разбор — **однократное** право, а не ежемесячный подарок. Пока его
 * считали в общем месячном окне, человек на Free получал новый «первый
 * разбор» каждое первое число: в сентябре израсходовал, в октябре снова ноль.
 * Постоянное окно делает право однократным по построению.
 */
export const START_WINDOW = 'start';

/**
 * Уровень доступа по подписке.
 *
 * Просроченная подписка тихо возвращает человека на Free: данные остаются,
 * пропадает только дополнительная глубина.
 */
export function tierOf(sub: { tier?: string; endsAt?: string | null } | null, now: string): Tier {
  if (!sub || !sub.tier || sub.tier === 'free') return 'free';
  if (sub.endsAt && sub.endsAt < now) return 'free';
  return sub.tier === 'pro' ? 'pro' : sub.tier === 'trial' ? 'trial' : 'free';
}
